import { Col, Collapse, Row, Stack } from 'react-bootstrap';
import {
  ArrayFields, Card, FormInput, FormMoneyInput, FormRadio, FormSelect, FormNumericInput,
  FormattedMessageDiv, formatTaxId, Tooltip
} from '@odmonorepo/core-components';
import { makeOptions, formatDateInput, validateDateComplete, validateDateFormat, validateNotFuture } from '@odmonorepo/odfunctions';
import * as yup from 'yup';
import { AppData } from 'merchant-api/types';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';


export const getBusinessDetailsSchema = () => {
  return yup.object().shape({
    persons: yup.array().of(
      yup.object().shape({
        typeData: yup.object().shape({
          ownershipPct: yup.number().required(),
          hasBeneficialOwner: yup.string().nullable()
          .transform((value, originalValue) => {
            return originalValue === "undefined" || originalValue === undefined ? null : value;
          })
          .when('ownershipPct', {
            is: (ownershipPct) => ownershipPct < 75,
            then: (schema) => schema.oneOf(['true', 'false']).required(),
            otherwise: (schema) => schema.notRequired(),
          }),
          hasManagingPerson: yup.string().nullable()
          .transform((value, originalValue) => {
            return originalValue === "undefined" || originalValue === undefined ? null : value;
          })
          .oneOf(['true', 'false']).required()
        })
      })
    ),
    businesses: yup.array().of(yup.object().shape({
      legalEntityType: yup.string().required(),
      inceptionDate: yup.string()
        .required('formInput.party.businesses.inceptionDate.error')
        .test('must be complete date', 'formInput.party.businesses.inceptionDate.error', validateDateComplete)
        .test('must be valid date', 'formInput.party.businesses.inceptionDate.error.invalidFormat', validateDateFormat)
        .test('must be in past', 'formInput.party.businesses.inceptionDate.error.futureDate', validateNotFuture),
      identifiers: yup.array().of(
        yup.object().shape({
          value: yup.string().when('type', (type, schema) =>
            type[0] === 'TAX_ID' ? schema.required().min(9) : schema
          )
        })
      )
    })),
    thirdPartyLenders: yup.mixed().nullable(),
  });
}

type BusinessDetailsProps = {
  showEstimatedAnnualRevenue?: boolean;
  review?: boolean;
  children?: ReactNode;
};

export const BusinessDetails = ({ showEstimatedAnnualRevenue, review = false, children }: BusinessDetailsProps) => {
  const { watch, control, formState } = useFormContext();

  const ownershipPct = watch('party.persons.0.typeData.ownershipPct');
  const lowOwnershipPctThreshold = 75
  const debouncedOwnershipPct = useDebounce(ownershipPct, 600); // 600ms debounce
  const isLowOwnershipPct = debouncedOwnershipPct > 0 && debouncedOwnershipPct < lowOwnershipPctThreshold;
  const intl = useIntl();

  const stf = watch('thirdPartyLenders.0.stf') === 'true';

  const businessIdentifiersFields =
    <ArrayFields<AppData> name={`party.businesses.0.identifiers`}>
      {({ fields }) => {
        return (
          <>
            {fields.map((idn, j) => {
              if (idn.type === 'TAX_ID') {
                return (
                  <FormInput<AppData> key={j} name={`party.businesses.0.identifiers.${j}.value`}
                    data-testid="businessTaxId"
                    stripFormatOnSave
                    formatValue={formatTaxId}
                    controlProps={{ inputMode: 'numeric' }}
                    label={<FormattedMessageDiv id='formInput.business.taxId' />}
                    labelTooltip={<Tooltip
                      style={{ pointerEvents: 'auto', display: 'inline' }}
                      title={intl.formatMessage({ id: 'formInput.business.taxId.tooltip' })} />}
                    helperText={intl.formatMessage({ id: 'formInput.business.taxId.helperText' })} />
                );
              }
            }
            )}
          </>);
      }}

    </ArrayFields>;

  return (
    <Card className={review ? 'border-0 shadow-sm' : undefined} bodyClassName={review ? 'p-5' : undefined}>
      <Stack gap={5}>
        {review ? (
          <Stack gap={1}>
            <FormattedMessageDiv className="h4 mb-0" id="businessReview.details" />
            <FormattedMessageDiv className="fs-6" id="businessReview.details.description" />
          </Stack>
        ) : (
          <FormattedMessageDiv className="h4" id={'renewalApp.businessDetails'} />
        )}
        <FormInput<AppData>
          name="party.businesses.0.inceptionDate"
          data-testid="businessInceptionDate"
          formatValue={formatDateInput}
          label={<FormattedMessageDiv id={`formInput.party.businesses.inceptionDate`} />}
          helperText={<FormattedMessageDiv id="formInput.party.businesses.inceptionDate.helperText" />}
          controlProps={{ inputMode: 'numeric' }}
          useYupErrors={true}
          className="label-expanded"
        />
        <FormSelect<AppData>
          name="party.businesses.0.legalEntityType"
          data-testid="businessLegalEntityType"
          options={makeOptions('business.legalEntityType', 7)}
          className="select-multiline"
          blankLabel=" "
        />
        <FormSelect<AppData>
          name="desiredUseOfFunds"
          data-testid="desiredUseOfFunds"
          options={makeOptions('financeNeeds.desiredUseOfFunds', 6)}
          blankLabel=""
          helperText
          className="label-expanded" />
        {businessIdentifiersFields}
        <FormNumericInput
          name="party.persons.0.typeData.ownershipPct"
          label={<FormattedMessageDiv id='formInput.party.persons.typeData.ownershipPct' />}
          data-testid="ownershipPct"
          suffix="%"
          min={1}
          max={100}
          className="label-expanded"
        />
        <Collapse in={isLowOwnershipPct}>
          <Row xs={1} md={1} className="g-3">
            <Col><FormRadio name="party.persons.0.typeData.hasBeneficialOwner" data-testid="hasBeneficialOwner" options={['true', 'false']} inline={false} /></Col>
          </Row>
        </Collapse>
        <FormRadio name="party.persons.0.typeData.hasManagingPerson" data-testid="hasManagingPerson" options={['true', 'false']} inline={false} />
        {showEstimatedAnnualRevenue && (
          <FormMoneyInput<AppData>
            name="estimatedAnnualRevenue"
            className="label-expanded"
            data-testid="estimatedAnnualRevenue"
          />
        )}
        {children}
      </Stack>
    </Card>
  );
};

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export default BusinessDetails;
